// Filename: CookiePolicyPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookiePolicyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/appsytec-logo.png"
                            alt="Appsytec Logo"
                            className="h-10 w-auto"
                        />
                    </Link>
                    <Link to="/" className="text-gray-700 font-medium hover:text-red-500 transition-colors">
                        Home
                    </Link>
                </div>
            </nav>

            <main className="flex-grow pt-24 pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none">
                        <h1 className="text-4xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
                        
                        <p className="text-gray-600 mb-8">
                            <strong>Last updated:</strong> January 2025
                        </p>

                        <div className="space-y-8">
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. What Are Cookies?</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Cookies are small text files that are placed on your device (computer, smartphone, tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners about user behaviour and preferences.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Cookies</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Appsytec uses cookies for several purposes:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>To ensure our website functions properly and securely</li>
                                    <li>To remember your preferences and settings</li>
                                    <li>To analyse how visitors use our website</li>
                                    <li>To improve our website performance and user experience</li>
                                    <li>To provide relevant content and advertisements</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
                                
                                <div className="space-y-6">
                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Necessary Cookies</h3>
                                        <p className="text-gray-700 leading-relaxed mb-3">
                                            These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility.
                                        </p>
                                        <p className="text-sm text-gray-600"><strong>Examples:</strong> Session cookies, authentication cookies, load balancing cookies</p>
                                        <p className="text-sm text-gray-600"><strong>Retention:</strong> Session or up to 1 year</p>
                                    </div>

                                    <div className="bg-blue-50 p-6 rounded-lg">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Analytics Cookies</h3>
                                        <p className="text-gray-700 leading-relaxed mb-3">
                                            These cookies collect information about how visitors use our website, such as which pages are visited most often and how users navigate the site. This data helps us improve our website's performance and user experience.
                                        </p>
                                        <p className="text-sm text-gray-600"><strong>Examples:</strong> Google Analytics cookies (_ga, _gid, _gat)</p>
                                        <p className="text-sm text-gray-600"><strong>Retention:</strong> Up to 2 years</p>
                                    </div>

                                    <div className="bg-green-50 p-6 rounded-lg">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Marketing Cookies</h3>
                                        <p className="text-gray-700 leading-relaxed mb-3">
                                            These cookies track visitors across websites to display relevant advertisements and measure the effectiveness of advertising campaigns. They may be set by us or third-party advertising partners.
                                        </p>
                                        <p className="text-sm text-gray-600"><strong>Examples:</strong> LinkedIn tracking pixels, Facebook Pixel, Google Ads cookies</p>
                                        <p className="text-sm text-gray-600"><strong>Retention:</strong> Up to 1 year</p>
                                    </div>

                                    <div className="bg-purple-50 p-6 rounded-lg">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">Functional Cookies</h3>
                                        <p className="text-gray-700 leading-relaxed mb-3">
                                            These cookies enable enhanced functionality and personalisation, such as remembering your preferences, language settings, or providing live chat features.
                                        </p>
                                        <p className="text-sm text-gray-600"><strong>Examples:</strong> Language preference cookies, live chat cookies, video player cookies</p>
                                        <p className="text-sm text-gray-600"><strong>Retention:</strong> Up to 1 year</p>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Some cookies on our website are set by third-party services:
                                </p>
                                
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Service</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Purpose</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b">Privacy Policy</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-4 py-3 text-sm text-gray-700">Google Analytics</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">Website analytics and performance tracking</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <a href="https://policies.google.com/privacy" className="text-red-500 hover:text-red-600 underline">
                                                        Google Privacy Policy
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-4 py-3 text-sm text-gray-700">LinkedIn</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">Social media integration and advertising</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <a href="https://www.linkedin.com/legal/privacy-policy" className="text-red-500 hover:text-red-600 underline">
                                                        LinkedIn Privacy Policy
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-4 py-3 text-sm text-gray-700">Facebook</td>
                                                <td className="px-4 py-3 text-sm text-gray-700">Social media integration and advertising</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <a href="https://www.facebook.com/privacy/policy/" className="text-red-500 hover:text-red-600 underline">
                                                        Facebook Privacy Policy
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Managing Your Cookie Preferences</h2>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookie Consent Banner</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    When you first visit our website, you'll see a cookie consent banner where you can:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                                    <li>Accept all cookies</li>
                                    <li>Reject all non-essential cookies</li>
                                    <li>Customise your cookie preferences</li>
                                </ul>

                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Browser Settings</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    You can also manage cookies through your browser settings:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                                    <li><strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
                                    <li><strong>Firefox:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
                                    <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
                                    <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
                                </ul>

                                <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Please note:</strong> Disabling certain cookies may affect the functionality of our website and limit your access to some features.
                                    </p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Updates to This Policy</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. When we make changes, we will update the "Last updated" date at the top of this policy and may notify you through our website or other communications.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Us</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-gray-700"><strong>Appsytec Pty Ltd</strong></p>
                                    <p className="text-gray-700">Email: contact@appsytec.com</p>
                                    <p className="text-gray-700">Location: Melbourne, Australia</p>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                            <span className="ml-2 text-xl font-bold">Appsytec Pty Ltd</span>
                        </div>
                        <p className="text-gray-400 mb-4">© 2025 Appsytec Pty Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CookiePolicyPage;